import React from 'react';

import { Link } from 'react-router-dom';

import { useBaseWidget } from 'hooks/useBaseWidget';

import { AssigneeAvatar } from 'components/common/AssigneeAvatar/AssigneeAvatar';
import { CustomizedStatusSelectWrapper } from 'components/common/CustomizedStatusSelect/CustomizedStatusSelectWrapper/CustomizedStatusSelectWrapper';
import { TooltipPosition } from 'components/common/CustomizedTooltip/customizedTooltip.enums';
import { ExportDropdown } from 'components/common/ExportDropdown/exportDropdown';
import NoData from 'components/common/NoData/noData';
import OverlayWithSpinner from 'components/common/OverlayWithSpinner/overlayWithSpinner';
import { ShareInsightButton } from 'components/common/ShareInsightButton/ShareInsightButton';
import { WidgetWithTitle } from 'components/common/WidgetWithTitle/WidgetWithTitle';
import { InformationBoxes } from 'components/common/informationBoxes/informationBoxes';

import { AddWidget } from 'components/Dashboards/Project/Components/CustomDashboard/AddRemoveWidget/addWidget';
import { PinboardWidgetPills } from 'components/Dashboards/Project/Components/CustomDashboard/PinboardWidgetPills/PinboardWidgetPills';
import { IComponentProps } from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import { CustomizedTooltip, LoaderContainer } from 'components/common';
import { useFinishDateOverallSummaryLogic } from 'components/widgets/FinishDateOverallSummary/FinishDateOverallSummary.logic';
import classes from 'components/widgets/FinishDateOverallSummary/FinishDateOverallSummary.module.scss';
import { useFinishDateOverallSummaryProvider } from 'components/widgets/FinishDateOverallSummary/FinishDateOverallSummary.provider';
import {
    BoxLabels,
    displayProbability,
    formatVarianceString,
} from 'components/widgets/FinishDateOverallSummary/FinishDateOverallSummary.utils';

const title = 'Finish Date Prediction';
const id = 'finishDate-overall-summary';
const componentKey = 'finishDateOverallSummary';

export const FinishDateOverallSummary = ({
    projectId,
    contractId,
    latestVersionId,
    globalFilterData,
    widgetId,
    localFilters,
    externalComponents = null,
    editNarrative = null,
    setSubTitleForExport,
}: IComponentProps) => {
    const { subTitle, route } = useBaseWidget({
        id,
        widgetId,
        projectId,
        contractId,
        latestVersionId,
        title,
        setSubTitleForExport,
    });
    const { ui, api } = useFinishDateOverallSummaryProvider({
        widgetId,
        globalFilterData,
        localFilters,
        latestVersionId,
        componentKey,
    });
    const { logic } = useFinishDateOverallSummaryLogic({ api });

    const activityPredictionData = api.getActivityPrediction.data;
    const isFetchingActivityPrediction = api.getActivityPrediction.isFetching;

    return (
        <WidgetWithTitle
            title={title}
            id={id}
            tooltip={api.getActivityPrediction.data?.info}
            titleComponents={[
                externalComponents && <div key={'externalComponents'}>{externalComponents}</div>,
                <AddWidget
                    key={'AddWidget'}
                    componentKey={componentKey}
                    title={title}
                    projectId={projectId}
                    contractId={contractId}
                    widgetId={widgetId}
                    route={route}
                    localFilters={ui.state}
                />,
                <ShareInsightButton key={'shareInsight'} title={title} link={route} />,
                <ExportDropdown key={'export'} title={title} subTitle={subTitle} />,
            ]}
            titleFilters={[
                editNarrative && <div key={'editNarrative'}>{editNarrative}</div>,
                widgetId && ui.pills.length > 0 ? (
                    <PinboardWidgetPills key={'widgetFilters'} widgetId={widgetId} filters={ui.pills} />
                ) : null,
            ]}
        >
            {isFetchingActivityPrediction && (
                <LoaderContainer height={'120px'}>
                    <OverlayWithSpinner />
                </LoaderContainer>
            )}
            {!isFetchingActivityPrediction && !activityPredictionData && (
                <NoData>
                    To view prediction for the <strong>Project Finish</strong> activity select the{' '}
                    <strong>first</strong> activity in filter list above.
                    <br />
                    To view prediction for a <strong>specific</strong> milestone/activity select it from the filter list
                    above.
                </NoData>
            )}

            {!isFetchingActivityPrediction &&
                activityPredictionData &&
                !activityPredictionData.assignee &&
                !activityPredictionData.status &&
                !activityPredictionData.activityId &&
                !activityPredictionData.name && <NoData />}

            {!isFetchingActivityPrediction &&
                activityPredictionData &&
                activityPredictionData.assignee &&
                activityPredictionData.status &&
                activityPredictionData.activityId &&
                activityPredictionData.name && (
                    <InformationBoxes className={`${classes.row}`}>
                        <InformationBoxes.Box className={classes.twiceTheSize}>
                            <div data-testid={'summary-activity-box'} className={classes.activityBox}>
                                <div className={classes.activitySmallTitle}>{BoxLabels.activity}</div>
                                <div className={classes.activityContainer}>
                                    <div className={classes.activityParent}>
                                        <div className={classes.activityNameDetails}>
                                            <CustomizedTooltip
                                                tooltipContent={activityPredictionData.name}
                                                triggerElement={
                                                    activityPredictionData.taskId ? (
                                                        <Link
                                                            to={{
                                                                pathname: `${logic.pathname}/card-${activityPredictionData.taskId}${activityPredictionData.isVirtual ? '-1' : ''}`,
                                                            }}
                                                            className={classes.activityLink}
                                                            data-testid={'activity-link'}
                                                        >
                                                            <div className={classes.tooltipContentParent}>
                                                                <div className={classes.tooltipContent}>
                                                                    {activityPredictionData.name}
                                                                </div>
                                                            </div>
                                                        </Link>
                                                    ) : (
                                                        activityPredictionData.name
                                                    )
                                                }
                                            />
                                        </div>
                                        <div className={classes.status}>
                                            <CustomizedStatusSelectWrapper
                                                disabled={true}
                                                status={activityPredictionData.status}
                                                originalStatus={null}
                                                taskId={activityPredictionData.taskId}
                                                isVirtual={activityPredictionData.isVirtual}
                                                invalidateQueriesKey={['activityPrediction']}
                                            />
                                            <AssigneeAvatar assignee={activityPredictionData.assignee} />
                                        </div>
                                    </div>
                                </div>
                                <div className={classes.activityId}>{activityPredictionData.activityId}</div>
                            </div>
                        </InformationBoxes.Box>

                        <InformationBoxes.Box className={classes.plannedFinish}>
                            <div className={`${classes.box}`}>
                                <div className={classes.top}>
                                    <div className={classes.smallTitle}>{BoxLabels.currentPlannedFinish}</div>
                                    <div data-testid={'data-value-container'} className={classes.dataValueContainer}>
                                        <div className={classes.title}>
                                            <div className={classes.predictionProbability}>
                                                {logic.plannedFinishDate}
                                                <CustomizedTooltip
                                                    tooltipPosition={TooltipPosition.Right}
                                                    tooltipContent={'Likelihood of meeting this date'}
                                                    triggerElement={`(${displayProbability(activityPredictionData.probability)}%)`}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div data-testid={'baseline-finish-date'} className={classes.baselineFinish}>
                                    Baseline Finish{' '}
                                    {`${logic.baselineFinishDate} ${formatVarianceString(activityPredictionData?.baselineFinishVariance)}`}
                                </div>
                            </div>
                        </InformationBoxes.Box>

                        <InformationBoxes.Box className={classes.optimisticForecast}>
                            <div className={classes.box}>
                                <div className={classes.top}>
                                    <div className={classes.smallTitle}>{BoxLabels.optimisticForecast}</div>
                                    <div data-testid={'data-value-container'} className={classes.dataValueContainer}>
                                        <div className={classes.title}>
                                            {`${logic.optimisticForecastedData} ${formatVarianceString(activityPredictionData?.optimisticFinishVariance)}`}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </InformationBoxes.Box>

                        <InformationBoxes.Box className={classes.bestForecast}>
                            <div className={classes.box}>
                                <div className={classes.top}>
                                    <div className={classes.bestForecastTitle}>{BoxLabels.mostLikelyForecast}</div>
                                    <div data-testid={'data-value-container'} className={classes.dataValueContainer}>
                                        <div
                                            className={classes.bestForecastSmallTitle}
                                        >{`${logic.bestForecastedData} ${formatVarianceString(activityPredictionData?.bestFinishVariance)}`}</div>
                                    </div>
                                </div>
                            </div>
                        </InformationBoxes.Box>

                        <InformationBoxes.Box className={classes.pessimisticForecast}>
                            <div className={classes.box}>
                                <div className={classes.top}>
                                    <div className={classes.smallTitle}>{BoxLabels.pessimisticForecast}</div>
                                    <div data-testid={'data-value-container'} className={classes.dataValueContainer}>
                                        <div
                                            className={classes.title}
                                        >{`${logic.pessimisticForecasted} ${formatVarianceString(activityPredictionData?.pessimisticsFinishVariance)}`}</div>
                                    </div>
                                </div>
                            </div>
                        </InformationBoxes.Box>
                    </InformationBoxes>
                )}
        </WidgetWithTitle>
    );
};
